import { connect } from "react-redux";
import React, { useState } from "react";
import { Button, ModalHeader, ModalBody } from "reactstrap";
import { compose } from "redux";
import NavigationActions from "redux/navigation/actions";
import Loader from "components/Loader";
import Papa from "papaparse";
import PatotsavPreviewTable from "./PatotsavPreviewTable";
import { bulkUploadPatotsav } from "services/patotsavServices";
import * as Yup from "yup";

const validationSchema = Yup.object().shape({
  gregorianDate: Yup.string()
    .required("Gregorian date is required.")
    .typeError("Gregorian date must be a valid date."),
  country: Yup.object().shape({
    english: Yup.string().required("Please enter the country name in English"),
    gujarati: Yup.string().required(
      "Please enter the country name in Gujarati"
    ),
    hindi: Yup.string().required("Please enter the country name in Hindi"),
  }),
  location: Yup.object().shape({
    english: Yup.string().required("Please enter the location name in English"),
    gujarati: Yup.string().required(
      "Please enter the location name in Gujarati"
    ),
    hindi: Yup.string().required("Please enter the location name in Hindi"),
  }),
  date: Yup.object().shape({
    english: Yup.string().required("Please enter the date in English"),
    gujarati: Yup.string().required("Please enter the date in Gujarati"),
    hindi: Yup.string().required("Please enter the date in Hindi"),
  }),
});

const { success, error } = NavigationActions;
const ImportEvents = ({ onClose, token }) => {
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [errors, setErrors] = useState([]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      error("Please upload a valid CSV file.");
      return;
    }
    setLoading(true);
    try {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          const { data } = results;
          const validationErrors = [];
          const validData = [];

          for (const [index, row] of data.entries()) {
            try {
              // Validate each row using Yup schema
              const gregorianDate =  new Date(row.date).toISOString().split('T')[0];

              const transformedRow = {
                ...row,
                gregorianDate,
                country: {
                  english: row["country.english"] ? row["country.english"]?.replace(/_/g, ",") : "",
                  gujarati: row["country.gujarati"] ? row["country.gujarati"]?.replace(/_/g, ",") : "",
                  hindi: row["country.hindi"] ? row["country.hindi"]?.replace(/_/g, ",") : "",
                },
                location: {
                  english: row["location.english"] ? row["location.english"]?.replace(/_/g, ",") : "",
                  gujarati: row["location.gujarati"] ? row["location.gujarati"]?.replace(/_/g, ",") : "",
                  hindi: row["location.hindi"] ? row["location.hindi"]?.replace(/_/g, ",") : "",
                },
                date: {
                  english: row["date.english"] ? row["date.english"]?.replace(/_/g, ",") : "",
                  gujarati: row["date.gujarati"] ? row["date.gujarati"]?.replace(/_/g, ",") : "",
                  hindi: row["date.hindi"] ? row["date.hindi"]?.replace(/_/g, ",") : "",
                },
              };

              // Remove unused keys from the original row
              delete transformedRow["country.english"];
              delete transformedRow["country.gujarati"];
              delete transformedRow["country.hindi"];

              delete transformedRow["location.english"];
              delete transformedRow["location.gujarati"];
              delete transformedRow["location.hindi"];

              delete transformedRow["date.english"];
              delete transformedRow["date.gujarati"];
              delete transformedRow["date.hindi"];

              const validatedRow = await validationSchema.validate(transformedRow, {
                abortEarly: false,
              });
              validData.push(validatedRow);
            } catch (validationError) {
              console.error("Error validating CSV:", validationError);

              validationErrors.push({
                row: index + 1,
                errors: validationError.errors,
              });
            }
          }

          setPreviewData(validData);
          setErrors(validationErrors);
        },
      });
    } catch (error) {
      console.error("Error parsing CSV:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (errors.length > 0) {
      error("Please fix errors before submitting.");
      return;
    }
    if(previewData.length === 0) {
      error("Please upload a CSV file before submitting.");
      return;
    }
    try {
      // Send the valid data to the server for bulk upload
      const res = await bulkUploadPatotsav(token, {data:previewData});
      if (res?.success) {
        success(res?.message);
        onClose(true);
      } else {
        error(res?.message);
      }
    } catch (err) {
      console.error(err);
      alert("Error uploading data.");
    }
  };
  
  return (
    <>
      <ModalHeader toggle={() => onClose()}>
        Import CSV File for Bulk Patotsav
      </ModalHeader>
      <ModalBody>
        <div>
          <input type="file" accept=".csv" onChange={handleFileUpload} />
          <Button
            onClick={handleSubmit}
            className="btn form-button modaladdbutton"
            disabled={loading || errors.length > 0 || previewData.length === 0}
          >
            {loading ? "Uploading..." : "Upload"}
          </Button>

          <div>
            {previewData?.length > 0 && (
              <>
                <h4>Preview</h4>
                <PatotsavPreviewTable data={previewData} />
              </>
            )}
            {errors?.length > 0 && (
              <div>
                <h4>Errors</h4>
                {errors?.map((err) => (
                  <p key={err?.row}>
                    Row {err?.row}: {err?.errors?.join(", ") || "Invalid Data"}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      </ModalBody>
      {loading ? <Loader /> : ""}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    ...state.themeChanger,
    token: state.auth.accessToken,
  };
};

export default compose(connect(mapStateToProps, { success, error }))(
  ImportEvents
);
